import { useCallback, useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Radio,
  RadioGroup,
  Label,
} from "@fluentui/react-components";
import {
  useEnvironmentStore,
  EnvironmentName,
} from "@/store/useEnvironmentStore";
import styles from "@/components/ChangeEnvPopupDialog/ChangeEnvPopupDialog.module.css";
import type { Props } from "@/components/ChangeEnvPopupDialog/ChangeEnvPopupDialog.types";

const REVIEW_APP_LABEL = "Review App";

const DATA_OPTIONS = [
  { name: "Preproduction", env: EnvironmentName.Preproduction },
  { name: REVIEW_APP_LABEL, env: EnvironmentName.ReviewApp },
];

const ChangeEnvPopupDialog = memo(({ isVisible, onClose }: Props) => {
  const { t } = useTranslation();
  const {
    env: currentEnvName,
    reviewAppBranchName: storedBranchName,
    setEnv,
  } = useEnvironmentStore();

  const [branchName, setBranchName] = useState(storedBranchName || "");
  const [branchError, setBranchError] = useState("");
  const [selectedEnv, setSelectedEnv] = useState<string>(currentEnvName);

  useEffect(() => {
    setSelectedEnv(currentEnvName);
    setBranchName(storedBranchName || "");
  }, [storedBranchName, currentEnvName]);

  useEffect(() => {
    if (branchError && branchName.trim()) {
      setBranchError("");
    }
  }, [branchName, branchError]);

  const onEnvSubmit = useCallback(() => {
    const selectedItem = DATA_OPTIONS.find(
      (item) => item.env === selectedEnv || item.name === selectedEnv,
    );
    if (!selectedItem) return;

    const isReviewApp = selectedItem.env === EnvironmentName.ReviewApp;
    if (isReviewApp && !branchName.trim()) {
      setBranchError("Branch name is required");
      return;
    }

    setEnv(selectedItem.env, isReviewApp ? branchName : undefined);
    onClose();
  }, [selectedEnv, branchName, setEnv, onClose]);

  const isReviewAppSelected =
    selectedEnv === EnvironmentName.ReviewApp ||
    selectedEnv === REVIEW_APP_LABEL;

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(_, data) => !data.open && onClose()}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("Environment Settings")}</DialogTitle>
          <DialogContent>
            <div className={styles.container}>
              <div className={styles.flexColumn}>
                <RadioGroup
                  value={selectedEnv}
                  onChange={(_, data) => setSelectedEnv(data.value)}
                >
                  {DATA_OPTIONS.map((item) => (
                    <Radio key={item.env} value={item.env} label={item.name} />
                  ))}
                </RadioGroup>
                {isReviewAppSelected && (
                  <div className={styles.branchInputContainer}>
                    <Label htmlFor="branch-input">{t("Branch name")}</Label>
                    <Input
                      id="branch-input"
                      placeholder="e.g. feature/my-branch"
                      value={branchName}
                      onChange={(_, data) => setBranchName(data.value)}
                    />
                    {branchError && (
                      <span className={styles.errorText}>{branchError}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button appearance="primary" onClick={onEnvSubmit}>
              {t("Confirm")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
});

export default ChangeEnvPopupDialog;
